import React, { useState, useEffect, useRef } from "react";
import PartnerTabs from "./partnerTabs";
import { DotsThree } from "@phosphor-icons/react";
import CaseStudyCard from "./caseStudyCard";
import VideoSlider from "./VideoSlider";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const TabComponent = ({ data }) => {
  const [activeTab, setActiveTab] = useState("1");
  const [displayedText, setDisplayedText] = useState("");
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleTabs, setVisibleTabs] = useState([]);
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [visitedTabs, setVisitedTabs] = useState(new Set());
  const [hasPartnerAnimated, setHasPartnerAnimated] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const containerRef = useRef(null);

  const handleTabClick = (tabKey) => {
    setActiveTab(tabKey);
    setShowDropdown(false);
    if (tabKey === "4") {
      if (isFirstVisit) {
        setHasPartnerAnimated(false);
        setIsFirstVisit(false);
      } else {
        setHasPartnerAnimated(true);
      }
    }
  };

  const scroll = (direction) => {
    const container = containerRef.current;
    if (!container) return;
  
    const scrollAmount = container.offsetWidth * 1; 
  
    container.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  };
  
  const extractText = (content) => {
    return content
      ?.map((block) => {
        if (block.type === "paragraph") return block.children.map(formatText).join("");
        if (block.type === "list")
          return `<${block.format === "ordered" ? "ol" : "ul"} style="padding-left: 20px; list-style-type: ${
            block.format === "ordered" ? "decimal" : "disc"
          };">${block.children.map((item) => `<li>${item.children.map(formatText).join("")}</li>`).join("")}</${
            block.format === "ordered" ? "ol" : "ul"
          }>`;
        if (block.type === "heading")
          return `<h${Math.min(6, Math.max(3, block.level || 3))}>${block.children
            .map(formatText)
            .join("")}</h${Math.min(6, Math.max(3, block.level || 3))}>`;
        if (block.type === "quote") return `<blockquote>${block.children.map(formatText).join("")}</blockquote>`;
        if (block.type === "image")
          return `<img src="${block.image.url}" alt="${
            block.image.alternativeText || "Image"
          }" style="max-width:100%; height:auto;" />`;
        return "";
      })
      .join("\n");
  };

  const formatText = (child) => {
    let text = child.text || "";
    if (child.bold) text = `<b>${text}</b>`;
    if (child.italic) text = `<i>${text}</i>`;
    if (child.underline) text = `<u>${text}</u>`;
    if (child.strikethrough) text = `<s>${text}</s>`;
    if (child.code) text = `<code>${text}</code>`;
    if (child.type === "link")
      text = `<a href="${child.url}" target="_blank">${child.children.map(formatText).join("")}</a>`;
    return text;
  };

  const tabs = [
    {
      key: "1",
      label: data?.attributes?.feature_heading,
      // content: data?.attributes?.features?.map((f) => `${f.title}: ${f.subtitle}`).join("\n"),
      content: extractText(data?.attributes?.features?.[0]?.description)
    },
    // {
    //   key: "2",
    //   label: data?.attributes?.use_cases_heading,
    //   content: data?.attributes?.use_cases?.map((u) => `${u.title}: ${u.subtitle}`).join("\n"),
    // },
    // { key: "3", label: data?.attributes?.pricing?.title, content: extractText(data?.attributes?.pricing?.description) },
    ...(data?.attributes?.pricing ? [
      { key: "3", label: data?.attributes?.pricing?.title, content: extractText(data?.attributes?.pricing?.description) }
    ] : []),
    // { key: "4", label: "Partners", content: <PartnerTabs hasAnimated={hasPartnerAnimated} />, isComponent: true },
    ...(data?.attributes?.demo_video_heading ? [
      {
        key: "5",
        label: data?.attributes?.demo_video_heading,
        content: <VideoSlider projects={data?.attributes?.demo_video} />,
        isComponent: true,
      }
    ] : []),
    ...(data?.attributes?.case_studies ? [
      {
        key: "6",
        label: data?.attributes?.case_studies_heading,
        content: <CaseStudyCard projects={data?.attributes?.case_studies} />,
        isComponent: true,
      },
    ] : []),
    ...(data?.attributes?.security ? [
      {
        key: "7",
        label: data?.attributes?.security?.title,
        content: extractText(data?.attributes?.security?.description),
      },
    ] : []),
    // {
    //   key: "6",
    //   label: data?.attributes?.case_studies_heading,
    //   content: <CaseStudyCard projects={data?.attributes?.case_studies} />,
    //   isComponent: true,
    // },
    // {
    //   key: "7",
    //   label: data?.attributes?.security?.title,
    //   content: extractText(data?.attributes?.security?.description),
    // },
  ];

  useEffect(() => {
    setVisibleTabs(tabs.slice(0, 6));
    setHiddenTabs(tabs.slice(6));
    setActiveTab("1");
  }, [data]);

  useEffect(() => {
    const activeContent = tabs.find((tab) => tab.key === activeTab)?.content;
    setDisplayedText("");
    setCurrentIndex(0);

    if (typeof activeContent === "string") {
      if (!visitedTabs.has(activeTab)) {
        setWords(activeContent.split(" "));
      } else {
        setDisplayedText(activeContent);
        setWords([]);
      }
    } else {
      setWords([]);
    }
  }, [activeTab, data]);

  useEffect(() => {
    if (words.length > 0 && currentIndex < words.length) {
      const timer = setTimeout(() => {
        setDisplayedText((prev) => prev + " " + words[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timer);
    } else if (words.length > 0) {
      setVisitedTabs((prev) => new Set([...prev, activeTab]));
    }
  }, [currentIndex, words]);

  return (
    <div className="tabs-container">
      <div className="relative flex items-center">
      <button
        onClick={() => scroll("left")}
        className="md:hidden absolute left-[-5px]"
      >
        <CaretLeft size={20} />
      </button>
        <div className="product-tabs-container flex space-x-4" ref={containerRef}>
          {visibleTabs.map((tab) => (
            <div
              key={tab.key}
              className={`tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabClick(tab.key)}
            >
              {tab.label}
            </div>
          ))}
        </div>
        <button
        onClick={() => scroll("right")}
        className="md:hidden absolute right-0"
      >
        <CaretRight size={20} />
      </button>
        {hiddenTabs.length > 0 && (
          <div className="relative ml-auto">
            {/* <button className="ml-2 px-3 py-2 bg-gray-300 rounded-md" onClick={() => setShowDropdown(!showDropdown)}>
                            <DotsThree size={32} weight="bold" className="text-black" />
                        </button> */}

            {/* {showDropdown && (
                            <div className="absolute right-0 mt-2 p-2 bg-white partner-border shadow-md rounded-md min-w-max z-50">
                                {hiddenTabs.map(tab => (
                                    <div key={tab.key} className="px-4 py-2 cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                                        onClick={() => {
                                            setVisibleTabs([...hiddenTabs]);
                                            setHiddenTabs([...visibleTabs]);
                                            setActiveTab(tab.key);
                                            setShowDropdown(false);
                                        }}>
                                        {tab.label}
                                    </div>
                                ))}
                            </div>
                        )} */}
          </div>
        )}
      </div>
      <div className="tab-content">
        {tabs.find((tab) => tab.key === activeTab)?.isComponent ? (
          tabs.find((tab) => tab.key === activeTab)?.content
        ) : (
          <p dangerouslySetInnerHTML={{ __html: displayedText.replace(/\n/g, "<br />") }} />
        )}
      </div>
    </div>
  );
};

export default TabComponent;
